// Supabase proxy client - routes all auth and data requests through our backend
// to avoid direct connections to Supabase from the frontend

// Helper function to get current token
export const getAccessToken = (): string | null => {
  const key = 'sb-' + process.env.REACT_APP_SUPABASE_URL?.replace('https://', '') + '-auth-token';
  try {
    const json = localStorage.getItem(key);
    if (json) {
      const data = JSON.parse(json);
      return data.access_token || null;
    }
  } catch (error) {
    console.error('Error retrieving token:', error);
  }
  return null;
};

// Helper to convert API paths to full URLs
export const getApiUrl = (endpoint: string): string => {
  // Ensure the URL has the API base URL
  if (endpoint.startsWith('/api')) {
    return `${process.env.REACT_APP_PROXY_URL}${endpoint}`;
  }
  return endpoint;
};

// Helper function to store session (similar to what Supabase does)
const storeSession = (accessToken: string, refreshToken: string, user: any, expiresAt?: number) => {
  const key = 'sb-' + process.env.REACT_APP_SUPABASE_URL?.replace('https://', '') + '-auth-token';
  const data = {
    access_token: accessToken,
    refresh_token: refreshToken,
    expires_at: expiresAt || Math.floor(Date.now() / 1000) + 3600,
    user
  };
  
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Error storing session:', error);
  }
};

// Helper function to remove session (for logout)
const removeSession = () => {
  const key = 'sb-' + process.env.REACT_APP_SUPABASE_URL?.replace('https://', '') + '-auth-token';
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing session:', error);
  }
};

// Track auth state listeners
let authStateSubscribers: Array<(event: string, session: any) => void> = [];

// Function to trigger auth state change events
const triggerAuthChange = (event: string, session: any) => {
  for (const callback of authStateSubscribers) {
    callback(event, session);
  }
};

// Auth functions
export const auth = {
  // Get current session from localStorage (no network request)
  getSession: async () => {
    const key = 'sb-' + process.env.REACT_APP_SUPABASE_URL?.replace('https://', '') + '-auth-token';
    try {
      const json = localStorage.getItem(key);
      
      // Only reset authentication if we're on the login page without the authenticated flag
      const path = window.location.pathname;
      const hasAuthFlag = window.location.search.includes('authenticated=true');
      const isLoggingIn = localStorage.getItem('isLoginInProgress') === 'true';
      
      // Don't clear session if we're in the process of logging in
      if (path === '/login' && !hasAuthFlag && !isLoggingIn) {
        console.log('proxyClient: Login page detected without auth flag, but not clearing session during active login');
        // We're commenting out the session clearing to fix the login issue
        // removeSession();
        // return { data: { session: null }, error: null };
      }
      
      if (json) {
        const data = JSON.parse(json);
        console.log('proxyClient: Found session in localStorage, checking expiration');
        // Check if session is expired
        if (data.expires_at && data.expires_at < Math.floor(Date.now() / 1000)) {
          // Token expired, try to refresh
          console.log('proxyClient: Session expired, attempting refresh');
          if (data.refresh_token) {
            return await auth.refreshSession(data.refresh_token);
          }
          console.log('proxyClient: No refresh token, clearing session');
          removeSession();
          return { data: { session: null }, error: 'Session expired' };
        }
        console.log('proxyClient: Session is valid');
        return { 
          data: { 
            session: {
              access_token: data.access_token,
              refresh_token: data.refresh_token,
              expires_at: data.expires_at,
              user: data.user
            } 
          }, 
          error: null 
        };
      }
      console.log('proxyClient: No session found in localStorage');
      return { data: { session: null }, error: null };
    } catch (error) {
      console.error('Error getting session:', error);
      removeSession();
      return { data: { session: null }, error: 'Failed to get session' };
    }
  },
  
  // Refresh session using backend proxy
  refreshSession: async (refreshToken: string) => {
    try {
      const response = await fetch(`/api/auth/session?refresh_token=${refreshToken}`);
      const data = await response.json();
      
      if (data.error || !data.data.session) {
        removeSession();
        return { data: { session: null }, error: data.error };
      }
      
      const { access_token, refresh_token, user, expires_at } = data.data.session;
      storeSession(access_token, refresh_token, user, expires_at);
      
      return data;
    } catch (error) {
      console.error('Error refreshing session:', error);
      removeSession();
      return { data: { session: null }, error: 'Failed to refresh session' };
    }
  },
  
  // Set session manually (e.g., after login/signup)
  setSession: async ({ access_token, refresh_token }: { access_token: string, refresh_token: string }) => {
    try {
      console.log('proxyClient: Setting session');
      
      // Get the user data using the access token
      const apiUrl = getApiUrl('/api/auth/user');
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${access_token}`
        }
      });
      
      const userData = await response.json();
      
      if (!response.ok || !userData) {
        console.error('proxyClient: Error getting user with token:', userData?.error);
        return { data: { user: null }, error: userData?.error || 'Failed to get user data' };
      }
      
      // Store session in localStorage
      storeSession(access_token, refresh_token, userData);
      
      // Trigger auth state change event
      triggerAuthChange('SIGNED_IN', {
        access_token,
        refresh_token,
        user: userData
      });
      
      console.log('proxyClient: Session set successfully with user:', userData?.email);
      return { data: { user: userData }, error: null };
    } catch (error) {
      console.error('proxyClient: Error setting session:', error);
      return { data: { user: null }, error: 'Failed to set session' };
    }
  },
  
  // Sign out through backend proxy
  signOut: async () => {
    try {
      const token = getAccessToken();
      
      // First, immediately remove the session from local storage
      // This ensures that even if the API call fails, the user is logged out locally
      removeSession();
      
      // Then call backend to invalidate the session if token exists
      if (token) {
        try {
          const apiUrl = getApiUrl('/api/auth/signout');
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
          
          // Even if this fails, we'll still continue with local logout
          if (!response.ok) {
            console.log('Backend signout failed, but continuing with local logout');
          }
        } catch (error) {
          console.log('Error calling signout endpoint, but continuing with local logout');
        }
      }
      
      // Trigger auth change event immediately to update all components
      triggerAuthChange('SIGNED_OUT', null);
      
      return { error: null };
    } catch (error) {
      console.error('Error signing out:', error);
      // Still remove the session locally even if there was an error
      removeSession();
      
      // Trigger auth change event even if there was an error
      triggerAuthChange('SIGNED_OUT', null);
      
      return { error: 'Failed to sign out properly' };
    }
  },
  
  // Get current user through backend proxy
  getUser: async () => {
    const token = getAccessToken();
    
    if (!token) {
      return { data: { user: null }, error: null };
    }
    
    try {
      const apiUrl = getApiUrl('/api/auth/user');
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const userData = await response.json();
      
      if (!response.ok) {
        return { data: { user: null }, error: userData.error || 'Failed to get user' };
      }
      
      return { data: { user: userData }, error: null };
    } catch (error) {
      console.error('Error getting user:', error);
      return { data: { user: null }, error: 'Failed to get user' };
    }
  },
  
  // Setup auth change listener (simplified version)
  onAuthStateChange: (callback: (event: string, session: any) => void) => {
    // Add callback to subscribers list
    authStateSubscribers.push(callback);
    
    // Simple polling implementation
    let lastSession: any = null;
    let lastEvent = 'INITIAL';
    
    const checkForChanges = async () => {
      const { data } = await auth.getSession();
      const currentSession = data.session;
      
      // Determine event type
      let event = 'INITIAL';
      if (lastSession === null && currentSession !== null) {
        event = 'SIGNED_IN';
      } else if (lastSession !== null && currentSession === null) {
        event = 'SIGNED_OUT';
      } else if (lastSession !== null && currentSession !== null &&
                JSON.stringify(lastSession) !== JSON.stringify(currentSession)) {
        event = 'TOKEN_REFRESHED';
      }
      
      // Only trigger callback if there's a change
      if (event !== 'INITIAL' || lastEvent === 'INITIAL') {
        callback(event, currentSession);
        lastEvent = event;
      }
      
      lastSession = currentSession;
    };
    
    // Initial check
    checkForChanges();
    
    // Set up polling (every 30 seconds)
    const intervalId = setInterval(checkForChanges, 30000);
    
    // Return unsubscribe function
    return {
      unsubscribe: () => {
        clearInterval(intervalId);
        // Also remove from subscribers
        authStateSubscribers = authStateSubscribers.filter(cb => cb !== callback);
      }
    };
  }
};

// Profiles API
export const profiles = {
  getProfile: async (userId: string) => {
    const token = getAccessToken();
    
    if (!token) {
      return { data: null, error: 'No authentication token' };
    }
    
    try {
      const apiUrl = getApiUrl(`/api/auth/profiles/${userId}`);
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        return { data: null, error: result.error || 'Failed to get profile' };
      }
      
      return result;
    } catch (error) {
      console.error('Error getting profile:', error);
      return { data: null, error: 'Failed to get profile' };
    }
  },
  
  createProfile: async (profileData: { id: string, email: string, role?: string, status?: string }) => {
    const token = getAccessToken();
    
    if (!token) {
      return { data: null, error: 'No authentication token' };
    }
    
    try {
      const apiUrl = getApiUrl('/api/auth/profiles');
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profileData)
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        return { data: null, error: result.error || 'Failed to create profile' };
      }
      
      return result;
    } catch (error) {
      console.error('Error creating profile:', error);
      return { data: null, error: 'Failed to create profile' };
    }
  }
};

// HTTP helper methods
const http = {
  /**
   * Make a GET request to the API
   * @param endpoint API endpoint to call
   * @param params Optional query parameters
   * @returns Promise with response data and error
   */
  get: async (endpoint: string, params?: Record<string, any>) => {
    const token = getAccessToken();
    if (!token) {
      return { data: null, error: 'No authentication token' };
    }

    try {
      let url = endpoint;
      if (params) {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          queryParams.append(key, String(value));
        });
        url = `${url}?${queryParams.toString()}`;
      }

      // Convert to full URL with API prefix if needed
      url = getApiUrl(url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();

      if (!response.ok) {
        return { data: null, error: result.error || `Failed to fetch data: ${response.status}` };
      }

      return { data: result, error: null };
    } catch (error) {
      console.error(`Error making GET request to ${endpoint}:`, error);
      return { data: null, error: 'Failed to fetch data' };
    }
  },

  /**
   * Make a POST request to the API
   * @param endpoint API endpoint to call
   * @param body Request body
   * @returns Promise with response data and error
   */
  post: async (endpoint: string, body: any) => {
    const token = getAccessToken();
    if (!token) {
      return { data: null, error: 'No authentication token' };
    }

    try {
      // Convert to full URL with API prefix if needed
      const url = getApiUrl(endpoint);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const result = await response.json();

      if (!response.ok) {
        return { data: null, error: result.error || `Failed to post data: ${response.status}` };
      }

      return { data: result, error: null };
    } catch (error) {
      console.error(`Error making POST request to ${endpoint}:`, error);
      return { data: null, error: 'Failed to post data' };
    }
  },

  /**
   * Make a PUT request to the API
   * @param endpoint API endpoint to call
   * @param body Request body
   * @returns Promise with response data and error
   */
  put: async (endpoint: string, body: any) => {
    const token = getAccessToken();
    if (!token) {
      return { data: null, error: 'No authentication token' };
    }

    try {
      // Convert to full URL with API prefix if needed
      const url = getApiUrl(endpoint);

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const result = await response.json();

      if (!response.ok) {
        return { data: null, error: result.error || `Failed to update data: ${response.status}` };
      }

      return { data: result, error: null };
    } catch (error) {
      console.error(`Error making PUT request to ${endpoint}:`, error);
      return { data: null, error: 'Failed to update data' };
    }
  },

  /**
   * Make a DELETE request to the API
   * @param endpoint API endpoint to call
   * @returns Promise with response data and error
   */
  delete: async (endpoint: string) => {
    const token = getAccessToken();
    if (!token) {
      return { data: null, error: 'No authentication token' };
    }

    try {
      // Convert to full URL with API prefix if needed
      const url = getApiUrl(endpoint);

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Check if response has JSON content
      const contentType = response.headers.get('content-type');
      let result;
      if (contentType && contentType.includes('application/json')) {
        result = await response.json();
      } else {
        result = { success: response.ok };
      }

      if (!response.ok) {
        return { data: null, error: result.error || `Failed to delete data: ${response.status}` };
      }

      return { data: result, error: null };
    } catch (error) {
      console.error(`Error making DELETE request to ${endpoint}:`, error);
      return { data: null, error: 'Failed to delete data' };
    }
  },

  /**
   * Make a PATCH request to the API
   * @param endpoint API endpoint to call
   * @param body Request body
   * @returns Promise with response data and error
   */
  patch: async (endpoint: string, body: any) => {
    const token = getAccessToken();
    if (!token) {
      return { data: null, error: 'No authentication token' };
    }

    try {
      // Convert to full URL with API prefix if needed
      const url = getApiUrl(endpoint);

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const result = await response.json();

      if (!response.ok) {
        return { data: null, error: result.error || `Failed to patch data: ${response.status}` };
      }

      return { data: result, error: null };
    } catch (error) {
      console.error(`Error making PATCH request to ${endpoint}:`, error);
      return { data: null, error: 'Failed to patch data' };
    }
  }
};

// Create a simulated Supabase client interface that uses our proxy
const proxyClient = {
  auth,
  profiles,
  ...http, // Add HTTP methods to the client
  from: (table: string) => {
    // Currently only supporting 'profiles' table
    if (table === 'profiles') {
      return {
        select: (columns: string) => ({
          eq: (column: string, value: string) => ({
            single: () => profiles.getProfile(value),
            maybeSingle: () => profiles.getProfile(value)
          })
        }),
        insert: (data: any[]) => profiles.createProfile(data[0])
      };
    }
    
    // For other tables, return a dummy interface that returns errors
    return {
      select: () => ({
        eq: () => ({
          single: () => Promise.resolve({ data: null, error: 'Table access not implemented through proxy' }),
          maybeSingle: () => Promise.resolve({ data: null, error: 'Table access not implemented through proxy' })
        })
      }),
      insert: () => Promise.resolve({ data: null, error: 'Table access not implemented through proxy' })
    };
  }
};

export default proxyClient; 